import { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { Send, Search, WifiOff, Wifi, MessageSquare, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8002") + "/api";
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8002")
  .replace(/^http/, "ws") + "/api/messages/ws";

// ── Helpers ────────────────────────────────────────────────────────────────
function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return "Hier";
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { day: "2-digit", month: "2-digit" });
}

function initials(firstName, lastName) {
  return ((firstName?.[0] || "") + (lastName?.[0] || "")).toUpperCase() || "?";
}

// ── Composant principal ────────────────────────────────────────────────────
export default function MessagesPage() {
  const { user, token } = useAuth();

  // ── Nouveau contact (depuis sessionStorage, posé avant navigate("/messages")) ──
  // Initialisé UNE SEULE FOIS au montage. sessionStorage.removeItem consomme la valeur
  // immédiatement → même StrictMode ou double-render ne peut pas la re-lire.
  const [newContactConv, setNewContactConv] = useState(() => {
    try {
      const raw = sessionStorage.getItem("msg_pending_contact");
      if (!raw) return null;
      sessionStorage.removeItem("msg_pending_contact"); // consommer une seule fois
      const { id, name, order } = JSON.parse(raw);
      if (!id) return null;
      const parts = (name || "").split(" ");
      return {
        user: { id, first_name: parts[0] || "Vendeur", last_name: parts.slice(1).join(" ") || "", avatar_url: null },
        last_message: null, unread_count: 0, online: false, _new: true,
        _order: order || null,
      };
    } catch { return null; }
  });

  // Pré-remplir related_entity_id si le contact vient d'une commande
  const [relatedEntityId, setRelatedEntityId] = useState(newContactConv?._order || null);

  // État général
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(newContactConv?.user?.id || null);
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState({});
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [wsStatus, setWsStatus] = useState("connecting");

  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  // ── Chargement conversations ─────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/messages/conversations`);
      setConversations(data);
      // Si la conv réelle du nouveau contact arrive, supprimer l'entrée synthétique
      setNewContactConv(prev =>
        prev && data.some(d => d.user.id === prev.user.id) ? null : prev
      );
      const online = {};
      data.forEach(c => { online[c.user.id] = c.online; });
      setOnlineUsers(prev => ({ ...prev, ...online }));
    } catch (err) {
      console.error("Erreur chargement conversations:", err);
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  // Charger les conversations au montage
  useEffect(() => { loadConversations(); }, [loadConversations]);

  // ── Chargement historique d'une conversation ─────────────────────────────
  const PAGE_SIZE = 50;

  const loadHistory = useCallback(async (otherId) => {
    setLoadingMsgs(true);
    setMessages([]);
    setHasMore(false);
    try {
      const { data } = await axios.get(`${API_URL}/messages/history/${otherId}?limit=${PAGE_SIZE}`);
      setMessages(data);
      setHasMore(data.length === PAGE_SIZE);
      // Marquer comme lus via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "read", from: otherId }));
      }
      // Remettre unread à 0 localement
      setConversations(prev =>
        prev.map(c => c.user.id === otherId ? { ...c, unread_count: 0 } : c)
      );
    } catch (err) {
      console.error("Erreur chargement historique:", err);
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  const loadMoreMessages = useCallback(async () => {
    if (!selectedId || !hasMore || loadingMore) return;
    const oldest = messages[0];
    if (!oldest) return;
    setLoadingMore(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/messages/history/${selectedId}?limit=${PAGE_SIZE}&before=${encodeURIComponent(oldest.created_at)}`
      );
      setMessages(prev => [...data, ...prev]);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      console.error("Erreur chargement plus:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [selectedId, hasMore, loadingMore, messages]);

  const selectConversation = useCallback((otherId) => {
    setSelectedId(otherId);
    loadHistory(otherId);
  }, [loadHistory]);

  // ── Auto-scroll ──────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── WebSocket ────────────────────────────────────────────────────────────
  const connectWS = useCallback(() => {
    if (!token) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setWsStatus("connecting");
    const ws = new WebSocket(`${WS_BASE}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsStatus("open");
      // Ping toutes les 25 s pour garder la connexion
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 25000);
    };

    ws.onmessage = (event) => {
      let data;
      try { data = JSON.parse(event.data); }
      catch { return; }

      switch (data.type) {
        case "message": {
          const otherId =
            data.sender_id === user?.id ? data.recipient_id : data.sender_id;

          // Si la conv active correspond → ajouter le message
          if (otherId === selectedIdRef.current) {
            setMessages(prev => {
              // Éviter les doublons (le sender reçoit son propre msg)
              if (prev.some(m => m.id === data.id)) return prev;
              return [...prev, data];
            });
            // Marquer lu immédiatement
            if (data.sender_id !== user?.id && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "read", from: data.sender_id }));
            }
          }

          // Mettre à jour la liste des conversations
          setConversations(prev => {
            const exists = prev.find(c => c.user.id === otherId);
            const isActive = otherId === selectedIdRef.current;
            if (exists) {
              return prev.map(c => {
                if (c.user.id !== otherId) return c;
                return {
                  ...c,
                  last_message: data,
                  unread_count: isActive || data.sender_id === user?.id
                    ? 0
                    : c.unread_count + 1,
                };
              }).sort((a, b) =>
                new Date(b.last_message?.created_at) - new Date(a.last_message?.created_at)
              );
            }
            // Nouveau contact dans newContactConv → promouvoir en vraie conversation
            // sans recharger toute la liste (évite d'écraser l'état)
            setNewContactConv(prev2 => {
              if (prev2?.user.id === otherId) return null; // promouvoir → supprimer synthétique
              return prev2;
            });
            // Ajouter directement dans conversations sans appel API
            const newConv = {
              user: { id: otherId, first_name: "", last_name: "", avatar_url: null },
              last_message: data,
              unread_count: isActive ? 0 : 1,
              online: false,
            };
            return [newConv, ...prev];
          });
          break;
        }

        case "read_ack": {
          // L'autre a lu : mettre à jour is_read dans la conv active
          if (data.by === selectedIdRef.current) {
            setMessages(prev =>
              prev.map(m => m.sender_id === user?.id ? { ...m, is_read: true } : m)
            );
          }
          break;
        }

        case "online": {
          setOnlineUsers(prev => ({ ...prev, [data.user_id]: data.online }));
          setConversations(prev =>
            prev.map(c => c.user.id === data.user_id ? { ...c, online: data.online } : c)
          );
          break;
        }

        case "error":
          toast.error(data.detail || "Erreur WebSocket");
          break;

        case "pong":
          break;

        default:
          break;
      }
    };

    ws.onclose = () => {
      setWsStatus("closed");
      clearInterval(pingIntervalRef.current);
      // Reconnexion automatique après 3 s
      reconnectTimeoutRef.current = setTimeout(connectWS, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token, user?.id, loadConversations]);

  useEffect(() => {
    connectWS();
    return () => {
      clearInterval(pingIntervalRef.current);
      clearTimeout(reconnectTimeoutRef.current);
      wsRef.current?.close();
    };
  }, [connectWS]);

  // related_entity_id déjà initialisé depuis newContactConv._order (sessionStorage)

  // ── Envoi de message ─────────────────────────────────────────────────────
  const sendMessage = async (e) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || !selectedId) return;
    setInput("");

    // On n'envoie related_entity_id qu'une seule fois (premier message du contexte)
    const entityId = relatedEntityId;
    setRelatedEntityId(null); // consommé

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "send",
        to: selectedId,
        content,
        ...(entityId ? { related_entity_id: entityId } : {}),
      }));
    } else {
      // Fallback REST
      try {
        await axios.post(`${API_URL}/messages/send/${selectedId}`, {
          content,
          ...(entityId ? { related_entity_id: entityId } : {}),
        });
      } catch {
        toast.error("Message non envoyé");
        setInput(content);
      }
    }
  };

  // ── Données filtrées ─────────────────────────────────────────────────────
  // Combiner le nouveau contact (état séparé, jamais écrasé) + conversations API
  const allConvs = newContactConv
    ? [newContactConv, ...conversations.filter(c => c.user.id !== newContactConv.user.id)]
    : conversations;

  const filteredConvs = allConvs.filter(c => {
    const name = `${c.user.first_name} ${c.user.last_name}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const selectedConv = allConvs.find(c => c.user.id === selectedId);
  const totalUnread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);

  // ── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Navbar />

      <div className="flex-1 container mx-auto px-2 md:px-6 py-4 max-w-7xl"
           style={{ height: "calc(100vh - 80px)" }}>
        <Card className="h-full flex overflow-hidden shadow-xl bg-white border-0">

          {/* ── Sidebar conversations ──────────────────────────────────── */}
          <div className="w-full md:w-80 border-r bg-gray-50 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b bg-white">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-xl">
                  Messages
                  {totalUnread > 0 && (
                    <span className="ml-2 text-xs bg-orange-500 text-white rounded-full px-2 py-0.5">
                      {totalUnread}
                    </span>
                  )}
                </h2>
                {/* Indicateur WebSocket */}
                <span title={wsStatus === "open" ? "Connecté" : "Déconnecté"}
                      className={`flex items-center gap-1 text-xs ${
                        wsStatus === "open" ? "text-green-500" : "text-gray-400"
                      }`}>
                  {wsStatus === "open"
                    ? <Wifi className="h-4 w-4" />
                    : <WifiOff className="h-4 w-4" />}
                </span>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher..."
                  className="pl-9 bg-gray-100 border-0"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Liste */}
            <ScrollArea className="flex-1">
              {loadingConvs ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : filteredConvs.length === 0 ? (
                <div className="text-center py-12 text-gray-400 px-4">
                  <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucune conversation</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredConvs.map(conv => {
                    const isSelected = conv.user.id === selectedId;
                    const isOnline = onlineUsers[conv.user.id] ?? conv.online;
                    return (
                      <div
                        key={conv.user.id}
                        onClick={() => selectConversation(conv.user.id)}
                        className={`p-4 flex gap-3 cursor-pointer hover:bg-white transition-colors ${
                          isSelected ? "bg-white border-l-4 border-l-orange-500" : ""
                        }`}
                      >
                        <div className="relative flex-shrink-0">
                          <Avatar>
                            {conv.user.avatar_url
                              ? <img src={conv.user.avatar_url} alt="" className="rounded-full" />
                              : <AvatarFallback className="bg-orange-100 text-orange-600 font-bold">
                                  {initials(conv.user.first_name, conv.user.last_name)}
                                </AvatarFallback>
                            }
                          </Avatar>
                          {isOnline && (
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-0.5">
                            <span className={`font-semibold text-sm truncate ${
                              conv.unread_count > 0 ? "text-black" : "text-gray-700"
                            }`}>
                              {conv.user.first_name} {conv.user.last_name}
                            </span>
                            <span className="text-xs text-gray-400 whitespace-nowrap ml-1">
                              {formatTime(conv.last_message?.created_at)}
                            </span>
                          </div>
                          <p className={`text-sm truncate ${
                            conv.unread_count > 0 ? "font-medium text-gray-900" : "text-gray-500"
                          }`}>
                            {conv.last_message?.sender_id === user?.id ? "Vous : " : ""}
                            {conv.last_message?.content || "..."}
                          </p>
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="w-5 h-5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0 self-center">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* ── Zone de chat ──────────────────────────────────────────── */}
          <div className="flex-1 flex flex-col bg-white">
            {!selectedConv ? (
              /* État vide */
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <MessageSquare className="h-16 w-16 mb-4 opacity-20" />
                <p className="text-lg font-medium">Sélectionnez une conversation</p>
                <p className="text-sm mt-1">Vos messages apparaîtront ici</p>
              </div>
            ) : (
              <>
                {/* En-tête */}
                <div className="p-4 border-b flex items-center gap-3 bg-white shadow-sm">
                  <div className="relative">
                    <Avatar>
                      {selectedConv.user.avatar_url
                        ? <img src={selectedConv.user.avatar_url} alt="" className="rounded-full" />
                        : <AvatarFallback className="bg-orange-100 text-orange-600 font-bold">
                            {initials(selectedConv.user.first_name, selectedConv.user.last_name)}
                          </AvatarFallback>
                      }
                    </Avatar>
                    {onlineUsers[selectedConv.user.id] && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">
                      {selectedConv.user.first_name} {selectedConv.user.last_name}
                    </h3>
                    <span className={`text-xs flex items-center gap-1 ${
                      onlineUsers[selectedConv.user.id] ? "text-green-600" : "text-gray-400"
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${
                        onlineUsers[selectedConv.user.id] ? "bg-green-500" : "bg-gray-300"
                      }`} />
                      {onlineUsers[selectedConv.user.id] ? "En ligne" : "Hors ligne"}
                    </span>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4 bg-gray-50/40">
                  {loadingMsgs ? (
                    <div className="flex justify-center py-10">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
                      <MessageSquare className="h-10 w-10 mb-2 opacity-20" />
                      <p className="text-sm">Aucun message — commencez la conversation !</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Bouton charger plus */}
                      {hasMore && (
                        <div className="flex justify-center pb-2">
                          <Button
                            size="sm" variant="ghost"
                            className="text-xs text-gray-500 hover:text-gray-700"
                            onClick={loadMoreMessages}
                            disabled={loadingMore}
                          >
                            {loadingMore
                              ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Chargement…</>
                              : "↑ Charger les messages précédents"}
                          </Button>
                        </div>
                      )}
                      {messages.map((msg) => {
                        const isMe = msg.sender_id === user?.id;
                        return (
                          <div key={msg.id} className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                            {/* Contexte commande si related_entity_id présent */}
                            {msg.related_entity_id && (
                              <div className={`mb-1 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${
                                isMe ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-500"
                              }`}>
                                📦 Commande #{msg.related_entity_id.slice(0, 8)}
                              </div>
                            )}
                            <div className={`max-w-[72%] rounded-2xl px-4 py-2.5 shadow-sm ${
                              isMe
                                ? "bg-orange-500 text-white rounded-br-none"
                                : "bg-white text-gray-800 border rounded-bl-none"
                            }`}>
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                              <div className={`flex items-center justify-end gap-1 mt-1 ${
                                isMe ? "text-orange-100" : "text-gray-400"
                              }`}>
                                <span className="text-[10px]">{formatTime(msg.created_at)}</span>
                                {isMe && (
                                  <span className="text-[10px]">
                                    {msg.is_read ? "✓✓" : "✓"}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Saisie */}
                <div className="p-3 bg-white border-t">
                  {wsStatus === "closed" && (
                    <p className="text-xs text-gray-400 text-center mb-2 flex items-center justify-center gap-1">
                      <WifiOff className="h-3 w-3" /> Reconnexion en cours...
                    </p>
                  )}
                  <form onSubmit={sendMessage} className="flex gap-2">
                    <Input
                      placeholder="Écrivez votre message..."
                      className="flex-1 bg-gray-50 border-gray-200 focus-visible:ring-orange-500"
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          sendMessage(e);
                        }
                      }}
                    />
                    <Button
                      type="submit"
                      className="bg-orange-500 hover:bg-orange-600"
                      disabled={!input.trim()}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </form>
                </div>
              </>
            )}
          </div>

        </Card>
      </div>
    </div>
  );
}
